// The paired Macs (docs/features/several-macs.md): every room this browser has paired with, each on its own
// WebRTC link (rtc.js) with its own reconnect token, like the headset's Home keeps a connection per Mac. They are
// connected lazily — the one you used last first, the rest after it, a moment apart — and each one that drops
// or cannot be reached is retried with a growing pause, showing offline meanwhile without holding up the others.
//
// One seam: `new Macs(hooks)` → `list`, `get(key)`, `current`, `choose(key)`, `forget(key)`, `pause(key)`,
// `resume(key)`, `retry(key)`, `verify(key)`, `retryRelay()`, `start()`, `reconnectAll()`. The app hears back through the hooks:
// onChange(mac, what), onConnected(mac) (the link is up, `mac.client` is new), onClosed(mac, transport),
// onEvent(mac, event), onStatus(mac, text).
//
// Paused (Josh, 09-29: not every paired Mac should connect at once): a paused Mac's link is closed and nothing
// knocks on its door — no reconnects, no passkey — until Resume. Kept per browser in `cody.paused` (room keys).
//
// Cody's relay refused (docs/features/relay-sign-in.md): `mac.relay` = {code, why, retryAfter} from rtc.js, for
// Home's action card; the retry waits by code (an hour's allowance, an invite still to redeem) and `retryRelay()`
// cuts that short when the person comes back from fixing it.
//
// The Mac asks for the passkey (the default for every new session — docs/features/headset-webrtc.md "Always ask"):
// the prompt is shown once by itself. Turned down, it is not shown again by a retry: `mac.relay` is
// {code: 'needs-passkey'}, Home shows the card with **Verify with passkey**, and `verify(key)` — that button — is
// what asks again; the connection then carries on by itself. A prompt that was not shown because the page was
// hidden is asked once when the page is in front again.
import { savedRooms, forgetRoom, lastMac, rememberLastMac } from './transport.js'
import { connectDirect, watchRoute } from './rtc.js'
import { Client } from './client.js'
import { watchIce, noteDrop, prefersRelay } from './linkhealth.js'
import { phoneBridge, askPhone } from './phonekey.js'

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

const PAUSED = 'cody.paused'
const pausedKeys = () => { try { return new Set(JSON.parse(localStorage.getItem(PAUSED) || '[]')) } catch { return new Set() } }
function keepPaused(key, on) {
  try {
    const all = pausedKeys()
    if (on) all.add(key); else all.delete(key)
    if (all.size) localStorage.setItem(PAUSED, JSON.stringify([...all])); else localStorage.removeItem(PAUSED)
    tellPhone(all)
  } catch {}
}
// Inside Cody's Android app the phone's own links (its outbox's background try, the car, the watch it carries) pause with
// this page (WF-10, the wear flows 10-09): the whole set, on every change and once at load (StoreBridge `macs.paused`).
let phoneAsk = null
function tellPhone(all) {
  try {
    const bridge = phoneBridge(globalThis)
    if (!bridge) return
    phoneAsk = phoneAsk || askPhone(bridge, 5000, 'paused.')
    phoneAsk({ op: 'macs.paused', rooms: [...all] }).catch(() => {})
  } catch {}
}

/// The cards whose button asks the person again: the passkey in a browser, the phone's own prompt in the phone app.
const ASKS_PERSON = ['needs-passkey', 'needs-presence']

/// How long to wait before asking Cody's relay again, by why it refused (null: the usual backoff).
function relayPause(relay) {
  if (relay?.unreachable) return 20000   // the site could not check this device (its account service is down): soon again
  // the room's door: that Mac is listed under another invite — a linked Mac that shared this device can give it its own
  // (Manage ‹Mac› › Use this Mac's invite, MacLink/MacLinkInvite), and then this device is let in: soon again
  if (relay?.door && relay.code === 'needs-invite') return 60000
  switch (relay?.code) {
    case 'mac-unverified': return 60000
    case 'needs-passkey': case 'needs-presence': return 10 * 60000   // woken by verify() — the card's button — or by the page coming to the front
    case 'phone-unpaired': return 12 * 3600000   // the Mac doesn't know this phone's key: nothing to retry until it is paired again (Add a Mac reloads the app) or Retry
    case 'mac-refused': return 10 * 60000     // the Mac turned this device away (revoked): woken by retry() — the card's Retry
    case 'throttled': return Math.max(60, Math.min(relay.retryAfter || 300, 3600)) * 1000
    case 'needs-invite': case 'needs-pairing': case 'needs-plan': return 10 * 60000   // woken early by retryRelay()
    case 'relay-not-set-up': return 10 * 60000
    default: return null
  }
}

/// One paired Mac: its key (the room, or 'tunnel' for the older tunnel door), its name, and the live state.
export class Mac {
  constructor(key, name, room = null) {
    this.key = key
    this.name = name || 'a Mac'
    this.room = room
    this.site = null         // Even mode: the site the glasses paired it on (else the app's own)
    this.device = null       // Even mode: that site's device id for this device (no cookie: rtc.js names it)
    this.client = null      // a Client once the link has come up (kept while offline: the last sessions it listed)
    this.link = 'connecting' // 'connecting' | 'online' | 'offline' | 'paused'
    this.why = ''            // why it is offline
    this.relay = null        // {code, why, retryAfter} when Cody's relay was needed and refused (rtc.js RELAY_CODES)
    this.paused = false      // Pause This Mac: no link, no attempts, until Resume
    this.route = null        // {kind, label} from rtc.js routeOf: how the bytes actually travel
    this.windows = null      // its MacWindows (the wire channel), set by the app
    this.license = null      // what it showed for a license, checked (license/show.js): null until there is a verdict
    this.status = ''         // the last thing its connection said
    this.verifyHeld = false  // a passkey prompt was turned down: none is shown again until Verify with passkey is pressed
    this.forgotten = false
  }
  get transport() { return this.client?.transport || null }
}

export class Macs {
  /// Inside the Even app (docs/flows/even-g2.yaml › One address — even/index.js): `rooms()` gives the Macs the glasses
  /// paired instead of this browser's saved rooms, each with its own `site` and `device` id, and `keyOnly` makes every
  /// connection prove itself with this device's key alone (rtc.js `passkey: false`). Elsewhere both are absent and
  /// nothing changes.
  constructor({ site, rooms = null, keyOnly = false, onChange = () => {}, onConnected = async () => {}, onClosed = () => {}, onEvent = () => {}, onStatus = () => {} } = {}) {
    this.site = site
    this.rooms = rooms
    this.keyOnly = keyOnly
    this.hooks = { onChange, onConnected, onClosed, onEvent, onStatus }
    this.list = []
    this.currentKey = null
    this.wakers = new Map()   // key → a function that cuts the current wait short (reconnectAll)
    // a Mac whose passkey could not be asked for while the page was hidden is asked when it is in front again
    if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      for (const mac of this.list) if (!mac.paused && mac.link !== 'online' && ASKS_PERSON.includes(mac.relay?.code) && mac.relay.hidden) this.wakers.get(mac.key)?.()
    })
  }

  /// The saved rooms, newest first (a Mac already in the list keeps its entry).
  loadRooms() {
    const paused = pausedKeys()
    tellPhone(paused)
    for (const r of this.rooms ? this.rooms() : savedRooms()) if (!this.get(r.room)) { const m = new Mac(r.room, r.mac, r.room); if (r.site) m.site = r.site; if (r.device) m.device = r.device; m.paused = paused.has(r.room); if (m.paused) m.link = 'paused'; this.list.push(m) }
    const last = lastMac()
    this.currentKey = this.get(last) && !this.get(last).paused ? last : (this.list.find((m) => !m.paused) || this.list[0])?.key || null
    return this
  }

  /// A Mac learned after start — a linked Mac named by a `devices` answer (linked-macs.js) — joins the list and
  /// connects now, not at the next launch (Josh 10-14: the mini never showed on the phone until a reload). The
  /// rooms already listed are left as they are. Returns the keys it added.
  adopt() {
    const before = new Set(this.list.map((m) => m.key))
    this.loadRooms()
    const fresh = this.list.filter((m) => m.room && !before.has(m.key))
    for (const mac of fresh) { this.hooks.onChange(mac, 'added'); this.run(mac) }
    return fresh.map((m) => m.key)
  }

  /// The Macs that are not paused: what pickers offer.
  get active() { return this.list.filter((m) => !m.paused) }

  /// Pause This Mac: its link closes, its loop sits until Resume; the choice survives reloads.
  pause(key) {
    const mac = this.get(key)
    if (!mac || mac.paused) return
    mac.paused = true
    keepPaused(key, true)
    this.drop(mac)
    this.wakers.get(key)?.()
    if (this.currentKey === key) { const other = this.active[0]; if (other) this.currentKey = other.key }
    this.set(mac, 'paused')
  }

  resume(key) {
    const mac = this.get(key)
    if (!mac || !mac.paused) return
    mac.paused = false
    keepPaused(key, false)
    this.set(mac, 'connecting')
    this.wakers.get(key)?.()
  }

  /// Try this Mac again now (a Retry button), cutting its wait short.
  retry(key) { const mac = this.get(key); if (mac && !mac.paused) this.wakers.get(key)?.() }

  /// A `devices` answer named these linked Macs (linked-macs.js): one waiting out its door's refusal (needs-invite —
  /// its Mac may just have taken the invite this device came under) tries now, not after its pause.
  retryRefused(keys) {
    for (const key of Array.isArray(keys) ? keys : []) {
      const mac = this.get(key)
      if (mac && !mac.paused && mac.link !== 'online' && mac.relay?.code === 'needs-invite') this.wakers.get(key)?.()
    }
  }

  /// Verify with passkey (the `needs-passkey` card's button): this Mac may show its passkey prompt again, now.
  verify(key) { const mac = this.get(key); if (!mac || mac.paused) return; mac.verifyHeld = false; this.wakers.get(key)?.() }

  /// The person may have fixed what Cody's relay asked for (redeemed an invite, paired): the Macs waiting on it
  /// try again now.
  retryRelay() {
    for (const mac of this.list) if (!mac.paused && mac.link !== 'online' && ['needs-invite', 'needs-pairing', 'needs-plan'].includes(mac.relay?.code)) this.wakers.get(mac.key)?.()
  }

  /// Close a Mac's live link (the channel's close handler ends its stream, which its loop is waiting on).
  drop(mac) {
    const t = mac.transport
    if (t?.close && mac.link === 'online') { t.close(); if (t.channel && t.channel.readyState !== 'closed') t.channel.onclose?.() }
  }

  add(mac) { if (!this.get(mac.key)) this.list.push(mac); if (!this.currentKey) this.currentKey = mac.key; return mac }
  get(key) { return key ? this.list.find((m) => m.key === key) || null : null }
  get current() { return this.get(this.currentKey) || this.list[0] || null }
  get several() { return this.list.length > 1 }

  /// A Mac chosen where one has to be (New Session, Mac Windows, Files on a Mac): it becomes the default, remembered.
  choose(key) {
    if (!this.get(key) || this.get(key).paused) return
    this.currentKey = key
    rememberLastMac(key)
    this.hooks.onChange(this.get(key), 'current')
  }

  set(mac, link, why = '') {
    mac.link = link; mac.why = why
    if (link !== 'online') mac.route = null
    if (link === 'online' || link === 'paused') { mac.relay = null; mac.verifyHeld = false }
    this.hooks.onChange(mac, 'link')
  }

  /// Forget This Mac: its room and token go, its link is closed, and it leaves the list.
  forget(key) {
    const mac = this.get(key)
    if (!mac) return
    mac.forgotten = true
    try { mac.transport?.close?.() } catch {}
    if (mac.room) forgetRoom(mac.room)
    this.list.splice(this.list.indexOf(mac), 1)
    this.wakers.get(key)?.()
    if (this.currentKey === key) this.currentKey = this.get(lastMac())?.key || this.list[0]?.key || null
    this.hooks.onChange(mac, 'forgotten')
  }

  /// The one used last first; the others once its first try has settled (or after eight seconds), a little apart,
  /// so opening the app doesn't knock on every door at once.
  async start() {
    const rooms = this.list.filter((m) => m.room)
    for (const mac of rooms) if (mac.paused) this.run(mac)   // a paused Mac's loop only sits and waits for Resume
    const waiting = rooms.filter((m) => !m.paused)
    const first = waiting.find((m) => m.key === this.currentKey) || waiting[0]
    if (!first) return
    const settled = new Promise((resolve) => { first.settled = resolve })
    this.run(first)
    await Promise.race([settled, sleep(8000)])
    for (const mac of waiting) {
      if (mac === first || mac.forgotten || mac.paused) continue
      this.run(mac)
      await sleep(600)
    }
  }

  /// Drop every link and connect again at once (the relay-only switch changed). Paused Macs stay paused.
  reconnectAll() {
    for (const mac of this.list) {
      if (mac.paused) continue
      this.drop(mac)
      this.wakers.get(mac.key)?.()
    }
  }

  /// A pause the reconnect loop takes, cut short by forget or reconnectAll.
  wait(mac, ms) {
    return new Promise((resolve) => {
      const t = setTimeout(done, ms)
      function done() { clearTimeout(t); resolve() }
      this.wakers.set(mac.key, done)
    })
  }

  /// Where and as whom a Mac is reached: this site, or (Even mode) the site it paired on with that site's device id;
  /// `passkey: false` there. Outside the Even app exactly what it always was: {site}.
  reach(mac) {
    const r = { site: mac.site || this.site }
    if (mac.device) r.device = mac.device
    if (this.keyOnly) r.passkey = false
    return r
  }

  /// One Mac's link, forever: connect, list its sessions, follow its events; when it drops, wait and try again.
  async run(mac) {
    let delay = 2000
    while (!mac.forgotten) {
      if (mac.paused) {
        mac.settled?.(); mac.settled = null
        if (mac.link !== 'paused') this.set(mac, 'paused')
        await this.wait(mac, 24 * 3600000)   // woken by Resume (or Forget)
        delay = 2000
        continue
      }
      this.set(mac, 'connecting', '')
      let direct
      try {
        direct = await connectDirect({ ...this.reach(mac), room: mac.room, preferRelay: prefersRelay(mac.room), stopped: () => mac.paused || mac.forgotten, mayPrompt: () => !mac.verifyHeld, onStatus: (s) => { mac.status = s; this.hooks.onStatus(mac, s) } })
      } catch (e) {
        mac.settled?.(); mac.settled = null
        if (mac.forgotten) return
        if (mac.paused) continue
        const msg = e.message || String(e)
        mac.relay = e.relay || null
        // a passkey prompt turned down: no Mac shows another by itself (several Macs queue theirs — one "no"
        // answers them all); each has its card, and its button asks again
        if (ASKS_PERSON.includes(mac.relay?.code) && !mac.relay.hidden) for (const other of this.list) if (other.link !== 'online') other.verifyHeld = true
        const pause = relayPause(mac.relay) ?? delay
        this.set(mac, 'offline', mac.relay ? mac.relay.why : /^Relay only/.test(msg) ? msg : 'The Mac isn’t answering.')
        this.hooks.onStatus(mac, `Could not reach ${mac.name} (${msg}) — trying again in ${pause >= 60000 ? Math.round(pause / 60000) + ' min' : Math.round(pause / 1000) + ' s'}.${mac.relay ? '' : ' Is its WebRTC front on?'}`)
        await this.wait(mac, pause)
        if (!mac.relay || relayPause(mac.relay) == null) delay = Math.min(delay * 2, 60000)
        continue
      }
      if (mac.forgotten || mac.paused) { try { direct.close() } catch {} if (mac.forgotten) return; continue }
      delay = 2000
      // a reconnect never blanks what is shown (Josh 10-09): the open transcripts carry over to the new connection's
      // client and its fresh history is merged in (client.js adopt, openView); every window reads mac.client at
      // the moment it acts, so prompts and answers go over this connection
      const client = new Client(direct).adopt(mac.client)
      mac.client = client
      client.onChange = (what) => this.hooks.onChange(mac, what)
      client.onEvent((ev) => this.hooks.onEvent(mac, ev))
      let route = null
      const stopRoute = watchRoute(direct, (r) => { route = r || route; mac.route = r; this.hooks.onChange(mac, 'route') })
      const stopIce = watchIce(direct, { onStatus: (s) => { mac.status = s; this.hooks.onStatus(mac, s) } })
      // (each session window asks its history again over this connection — session.js goLive — and says so if it fails)
      this.set(mac, 'online')
      mac.settled?.(); mac.settled = null
      try { await this.hooks.onConnected(mac) } catch (e) { this.hooks.onStatus(mac, e.message || String(e)) }
      await client.stream()   // resolves when the channel closes
      stopRoute(); stopIce()
      // a direct path that keeps dropping: the relay for this Mac for the next hour (linkhealth.js)
      if (noteDrop(mac.room, { route, troubled: direct.troubled })) this.hooks.onStatus(mac, `The direct path to ${mac.name} keeps dropping — the relay for the next hour.`)
      this.hooks.onClosed(mac, direct)
      if (mac.forgotten) return
      if (mac.paused) continue
      this.set(mac, 'connecting')
      this.hooks.onStatus(mac, `The connection to ${mac.name} closed (WebRTC: ${direct.pc?.connectionState || '?'}, ICE ${direct.pc?.iceConnectionState || '?'}) — reconnecting…`)
      await this.wait(mac, 1000)
    }
  }
}
